// postAPI.js
import apiClient from "../authentication/utils/core/axios";
const PostAPI = {
  // ---------- Base / list ----------
  fetchAll: async () => {
    const res = await apiClient.get('/posts');
    if (res.status !== 200) throw new Error('Failed to fetch posts');
    return res.data;
  },

  loadMore: async (skip = 0, limit = 10, extra = {}) => {
    const res = await apiClient.get('/posts', { params: { skip, limit, ...extra } });
    if (res.status !== 200) throw new Error('Failed to load more posts');
    return {
      posts: res.data?.posts ?? res.data,
      hasMore:
        res.data?.has_more ??
        (Array.isArray(res.data) && res.data.length >= limit),
      meta: {
        current_page: res.data?.current_page,
        per_page: res.data?.per_page,
        sort_by: res.data?.sort_by,
        period: res.data?.period,
      }
    };
  },

  // ---------- Multi-filter fetch (returns shape reducer expects) ----------
  fetchWithFilters: async ({
    skip = 0,
    limit = 10,
    sort_by = 'latest',
    period = 'all',
    sentiment_filter = null,
    stock_symbol = null,
    replace = true,                // ✅ passthrough so reducer can decide append/replace
  }) => {
    let endpoint = '/posts';
    const params = { skip, limit, sort_by, period };
    if (sentiment_filter) params.sentiment_filter = sentiment_filter;
    if (stock_symbol) {
      endpoint = `/posts/stock/symbol/${encodeURIComponent(stock_symbol.toUpperCase())}`;
    }

    const res = await apiClient.get(endpoint, { params });
    if (res.status !== 200) {
      throw new Error(`Failed to fetch posts with filters: ${res.status}`);
    }

    return {
      data: res.data, // preserve backend shape ({posts, has_more, current_page, per_page, ...})
      filters: { skip, limit, sort_by, period, sentiment_filter, stock_symbol },
      replace,        // ✅ so extraReducer can read action.payload.replace
    };
  },

  // ---------- CRUD ----------
  create: async (newPostData) => {
    const res = await apiClient.post('/posts/', newPostData);
    if (res.status !== 201 && res.status !== 200) {
      throw new Error('Failed to create post');
    }
    return res.data;
  },

  update: async (postId, updatedPostData) => {
    const res = await apiClient.put(`/posts/${postId}`, updatedPostData);
    if (res.status !== 200) throw new Error('Failed to update post');
    return res.data;
  },

  delete: async (postId) => {
    const res = await apiClient.delete(`/posts/${postId}`);
    if (res.status !== 204 && res.status !== 200) {
      throw new Error('Failed to delete post');
    }
    return { id: postId };
  },

  // NOTE: the backend has no GET /posts/{post_id} route (only PUT/DELETE exist
  // on that path -- confirmed live, returns 405). There is currently no way to
  // fetch a single post by id; posts are only ever returned via list/filter
  // endpoints. Left as a clear error rather than pointed at a URL that 405s.
  fetchById: async () => {
    throw new Error('fetchById has no backend endpoint -- GET /posts/{post_id} does not exist (405)');
  },

  fetchUserPosts: async (userId, skip = 0, limit = 10) => {
    const res = await apiClient.get(`/users-history/${userId}/posts`, { params: { skip, limit } });
    if (res.status !== 200) throw new Error('Failed to fetch user posts');
    return res.data;
  },

  // ---------- Likes ----------
  like: async (postId) => {
    const res = await apiClient.post(`/posts/${postId}/like`);
    if (res.status !== 200) throw new Error('Failed to like post');
    return { ...res.data, post_id: res.data?.post_id ?? postId };
  },

  unlike: async (postId) => {
    const res = await apiClient.delete(`/posts/${postId}/like`);
    if (res.status !== 200 && res.status !== 204) throw new Error('Failed to unlike post');
    return { ...(res.data || {}), post_id: res.data?.post_id ?? postId };
  },

  // ---------- Sentiment scopes / filters ----------
  fetchBySentiment: async (sentimentType, skip = 0, limit = 10) => {
    const res = await apiClient.get(`/posts/sentiment/${encodeURIComponent(sentimentType)}`, {
      params: { skip, limit }
    });
    if (res.status !== 200) throw new Error('Failed to fetch sentiment posts');
    return res.data; // { posts, has_more, ... }
  },

  // ---------- Bull/Bear votes ----------
  bull: async (postId) => {
    const res = await apiClient.post(`/posts/${postId}/bull`);
    if (res.status !== 200) throw new Error('Failed to bull vote');
    return { ...res.data, post_id: res.data?.post_id ?? postId };
  },

  unlikebull: async (postId) => {
    const res = await apiClient.delete(`/posts/${postId}/bull`);
    if (res.status !== 200 && res.status !== 204) throw new Error('Failed to remove bull vote');
    const payload = res.status === 204 ? {} : (res.data || {});
    return { ...payload, post_id: payload.post_id ?? postId };
  },

  bear: async (postId) => {
    const res = await apiClient.post(`/posts/${postId}/bear`);
    if (res.status !== 200) throw new Error('Failed to bear vote');
    return { ...res.data, post_id: res.data?.post_id ?? postId };
  },

  unlikebear: async (postId) => {
    const res = await apiClient.delete(`/posts/${postId}/bear`);
    if (res.status !== 200 && res.status !== 204) throw new Error('Failed to remove bear vote');
    const payload = res.status === 204 ? {} : (res.data || {});
    return { ...payload, post_id: payload.post_id ?? postId };
  },

  // ---------- Stocks on post (backend route is singular /post, not /posts) ----------
  fetchStocksForPost: async (postId) => {
    const res = await apiClient.get(`/post/${postId}/stocks`);
    if (res.status !== 200) throw new Error('Failed to fetch post stocks');
    return res.data; // array of stocks
  },

  updateStocks: async (postId, stockIds) => {
    const res = await apiClient.put(`/post/${postId}/stocks`, { stock_ids: stockIds });
    if (res.status !== 200) throw new Error('Failed to update post stocks');
    return { postId, stockIds };
  },

  // ---------- Following ----------
  fetchFollowingPosts: async (skip = 0, limit = 10) => {
    const res = await apiClient.get('/posts/following', { params: { skip, limit } });
    if (res.status !== 200) throw new Error('Failed to fetch following posts');
    return res.data;
  },

  fetchUserHistoryPosts: async (userId, {
    skip = 0,
    limit = 10,
    sort_by = 'latest',
    period = 'all',
  } = {}) => {
    const res = await apiClient.get(`/users-history/${userId}/posts`, {
      params: { skip, limit, sort_by, period }
    });
    if (res.status !== 200) throw new Error('Failed to fetch user history posts');
    // Normalize to common shape the reducer already expects
    return {
      posts: res.data?.posts ?? res.data,
      has_more: res.data?.has_more ??
        (Array.isArray(res.data) && res.data.length >= limit),
      current_page: res.data?.current_page,
      per_page: res.data?.per_page ?? limit,
      sort_by: res.data?.sort_by ?? sort_by,
      period: res.data?.period ?? period,
      total: res.data?.total,
    };
  },

  // GroupPost is a separate table from Post -- fetchUserHistoryPosts above
  // can never include a user's group posts, regardless of skip/limit/sort.
  fetchUserGroupHistoryPosts: async (userId, { skip = 0, limit = 20 } = {}) => {
    const res = await apiClient.get(`/users-history/${userId}/group-posts`, {
      params: { skip, limit }
    });
    if (res.status !== 200) throw new Error('Failed to fetch user group history posts');
    return {
      posts: res.data?.posts ?? [],
      has_more: res.data?.has_more ?? false,
      total: res.data?.total ?? 0,
    };
  },
};

export default PostAPI;
